import { type VoiceIntent } from './voiceControl.types'

export type SupportedLocale = 'pt-BR' | 'en-US' | 'es-419'

const INTENT_ORDER: VoiceIntent[] = [
  'faster',
  'slower',
  'nextChapter',
  'prevChapter',
  'stop',
  'pause',
  'forward',
  'backward',
  'play'
]

const GRAMMAR: Record<SupportedLocale, Record<VoiceIntent, string[]>> = {
  'pt-BR': {
    play: [
      'iniciar',
      'continuar',
      'continua',
      'comecar',
      'comece',
      'tocar',
      'play',
      'ouvir'
    ],
    pause: ['pausar', 'pausa', 'pause'],
    stop: ['parar', 'pare', 'parada', 'fechar player', 'encerrar'],
    forward: ['avancar', 'avanca', 'adiante', 'frente', 'proxima'],
    backward: ['retroceder', 'voltar', 'volta', 'atras', 'retrocede'],
    nextChapter: ['proximo capitulo', 'proxima faixa', 'avancar capitulo'],
    prevChapter: ['capitulo anterior', 'faixa anterior', 'voltar capitulo'],
    faster: ['mais rapido', 'acelerar', 'acelera', 'rapido'],
    slower: ['mais devagar', 'desacelerar', 'devagar', 'lento']
  },
  'en-US': {
    play: ['play', 'continue', 'start', 'listen', 'resume'],
    pause: ['pause'],
    stop: ['stop', 'close player', 'end'],
    forward: ['forward', 'skip forward', 'ahead'],
    backward: ['backward', 'back', 'rewind'],
    nextChapter: ['next chapter', 'next track'],
    prevChapter: ['previous chapter', 'previous track', 'last chapter'],
    faster: ['faster', 'speed up', 'quicker'],
    slower: ['slower', 'slow down']
  },
  'es-419': {
    play: [
      'iniciar',
      'continuar',
      'continua',
      'sigue',
      'empezar',
      'comenzar',
      'escuchar',
      'play'
    ],
    pause: ['pausar', 'pausa'],
    stop: ['parar', 'detener', 'cerrar reproductor', 'alto'],
    forward: ['avanzar', 'avanza', 'adelante'],
    backward: ['retroceder', 'volver', 'atras'],
    nextChapter: ['siguiente capitulo', 'proxima pista'],
    prevChapter: ['capitulo anterior', 'pista anterior'],
    faster: ['mas rapido', 'acelerar', 'rapido'],
    slower: ['mas lento', 'despacio', 'lento']
  }
}

export function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
}

export function resolveLocale(language: string): SupportedLocale {
  const lang = language.toLowerCase()
  if (lang.startsWith('en')) return 'en-US'
  if (lang.startsWith('es')) return 'es-419'
  return 'pt-BR'
}

export function recognitionLang(locale: SupportedLocale): string {
  return locale
}

const MAX_REPEATS = 3

const REPEATABLE_INTENTS: VoiceIntent[] = ['forward', 'backward']

export interface VoiceCommand {
  intent: VoiceIntent
  repeat: number
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function countWholePhrase(normalized: string, phrase: string): number {
  const spaced = ` ${normalized.replace(/[^a-z0-9]+/g, ' ')} `
  const pattern = new RegExp(` ${escapeRegExp(phrase)}(?= )`, 'g')
  return (spaced.match(pattern) ?? []).length
}

export function matchCommand(
  transcript: string,
  locale: SupportedLocale
): VoiceCommand | null {
  const normalized = normalize(transcript)
  const grammar = GRAMMAR[locale]
  for (const intent of INTENT_ORDER) {
    const phrase = grammar[intent]
      .map(normalize)
      .find(candidate => normalized.includes(candidate))
    if (phrase === undefined) continue
    const spoken = REPEATABLE_INTENTS.includes(intent)
      ? countWholePhrase(normalized, phrase)
      : 1
    return { intent, repeat: Math.min(Math.max(spoken, 1), MAX_REPEATS) }
  }
  return null
}

export function matchIntent(
  transcript: string,
  locale: SupportedLocale
): VoiceIntent | null {
  return matchCommand(transcript, locale)?.intent ?? null
}
